import type * as React from "react";

/**
 * Agent identity mark. Deliberately unlike `Avatar`: people are round orbs,
 * agents are squared pixel plates. `generic` is the Salesko-hosted Generic
 * Agent; `private` is the owner's Private Agent, which runs on their own
 * computer. The two glyphs differ in shape as well as tone so the distinction
 * survives greyscale and small sizes.
 */
export interface AgentAvatarProps {
  /** Which agent this mark stands for. @default "generic" */
  variant?: "generic" | "private";
  /** @default "md" */
  size?: "xs" | "sm" | "md" | "lg";
  /** Accessible name when no visible label sits beside the mark. Omit for a decorative mark. */
  srLabel?: string;
  style?: React.CSSProperties;
}

const DIMS: Record<NonNullable<AgentAvatarProps["size"]>, number> = { xs: 24, sm: 32, md: 44, lg: 56 };

const VARIANTS: Record<NonNullable<AgentAvatarProps["variant"]>, { bg: string; fg: string; ring: string }> = {
  generic: { bg: "var(--accent-soft)", fg: "var(--accent)", ring: "var(--accent)" },
  private: { bg: "var(--entity-company-soft)", fg: "var(--entity-company)", ring: "var(--entity-company)" }
};

/**
 * Both glyphs are drawn on the same 12x12 pixel grid so they read as one
 * family. Generic is a broadcast core (centre block + orbiting pixels);
 * Private is a plate with a base, i.e. a machine sitting on a desk.
 */
function GenericGlyph({ fill }: { fill: string }) {
  return (
    <g fill={fill}>
      <rect x="5" y="5" width="2" height="2" />
      <rect x="2" y="2" width="2" height="2" />
      <rect x="8" y="2" width="2" height="2" />
      <rect x="2" y="8" width="2" height="2" />
      <rect x="8" y="8" width="2" height="2" />
      <rect x="5" y="1" width="2" height="1" />
      <rect x="5" y="10" width="2" height="1" />
      <rect x="1" y="5" width="1" height="2" />
      <rect x="10" y="5" width="1" height="2" />
    </g>
  );
}

function PrivateGlyph({ fill }: { fill: string }) {
  return (
    <g fill={fill}>
      <rect x="1" y="2" width="10" height="1" />
      <rect x="1" y="3" width="1" height="4" />
      <rect x="10" y="3" width="1" height="4" />
      <rect x="1" y="7" width="10" height="1" />
      <rect x="3" y="4" width="2" height="2" />
      <rect x="7" y="4" width="2" height="2" />
      <rect x="5" y="9" width="2" height="1" />
      <rect x="3" y="10" width="6" height="1" />
    </g>
  );
}

export function AgentAvatar({ variant = "generic", size = "md", srLabel, style }: AgentAvatarProps) {
  const d = DIMS[size];
  const tone = VARIANTS[variant];
  const decorative = !srLabel;

  return (
    <span
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      aria-label={srLabel}
      style={
        {
          width: `${d}px`,
          height: `${d}px`,
          flex: "none",
          display: "grid",
          placeItems: "center",
          borderRadius: "var(--radius-md)",
          background: tone.bg,
          border: `1px solid ${tone.ring}`,
          boxShadow: "var(--shadow-xs)",
          ...style
        } as React.CSSProperties
      }
    >
      <svg
        width={Math.round(d * 0.6)}
        height={Math.round(d * 0.6)}
        viewBox="0 0 12 12"
        shapeRendering="crispEdges"
        aria-hidden="true"
      >
        {variant === "generic" ? <GenericGlyph fill={tone.fg} /> : <PrivateGlyph fill={tone.fg} />}
      </svg>
    </span>
  );
}
